import React from "react";
import { Phone, Mail, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { BUSINESS } from "@/lib/content";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact Savage Cleaning — Free Quotes | Brisbane, Gold Coast & SEQ"
        description="Get in touch with Savage Cleaning for a free, no-obligation quote. Call 0416 238 430 or email info@savagecleaning.com."
      />

      {/* HERO */}
      <section className="relative py-24 sm:py-32 border-b border-white/5" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">Contact</span>
            </div>
            <h1 className="mt-6 font-serif-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Let's talk about your site or project.
            </h1>
            <p className="mt-8 text-lg text-white/80 leading-relaxed max-w-2xl">
              Send us the details and our team will personally review your enquiry. We aim to
              respond within one business day with a clear scope and tailored quote.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 sm:py-24" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Info */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <span className="eyebrow">Get in touch</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                Prefer to skip the form?
              </h2>
              <p className="mt-3 text-white/75 leading-relaxed">
                Call us directly, or send an email — whatever's easiest.
              </p>
            </div>

            <ul className="space-y-6">
              <li>
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="flex items-start gap-4 group"
                  data-testid="contact-phone"
                >
                  <span className="w-11 h-11 flex items-center justify-center border border-[#C9A24B]/50 text-[#C9A24B]">
                    <Phone size={18} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="eyebrow">Phone</div>
                    <div className="mt-1 text-white text-lg group-hover:text-[#C9A24B] transition-colors">
                      {BUSINESS.phone}
                    </div>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  className="flex items-start gap-4 group"
                  data-testid="contact-email"
                >
                  <span className="w-11 h-11 flex items-center justify-center border border-[#C9A24B]/50 text-[#C9A24B]">
                    <Mail size={18} strokeWidth={1.5} />
                  </span>
                  <div>
                    <div className="eyebrow">Email</div>
                    <div className="mt-1 text-white text-lg group-hover:text-[#C9A24B] transition-colors">
                      {BUSINESS.email}
                    </div>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-hours">
                <span className="w-11 h-11 flex items-center justify-center border border-[#C9A24B]/50 text-[#C9A24B]">
                  <Clock size={18} strokeWidth={1.5} />
                </span>
                <div className="w-full">
                  <div className="eyebrow">Business hours</div>
                  <ul className="mt-2 space-y-1 text-white/85">
                    {BUSINESS.hours.map((h) => (
                      <li key={h.day} className="flex justify-between gap-4 text-sm">
                        <span className="text-white/70">{h.day}</span>
                        <span>{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            </ul>

            <div className="pt-6 border-t border-white/10">
              <div className="eyebrow mb-2">Servicing</div>
              <p className="text-white/85">{BUSINESS.region}</p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="mb-6">
              <span className="eyebrow">Free quote</span>
              <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl text-white leading-tight">
                Request your no-obligation quote.
              </h2>
            </div>
            <QuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
